import { toast } from 'react-toastify';
import { GROUP_ROLE_NAME_MAP } from '@lp/features/BulkAssignment/constants';
import { BulkAssignmentCase, CaseUserGroup } from '@/api/apis/case';
import { BulkAssignmentFiltersType } from '../types';
import { isDisabledDetachOnly } from '../utils';

type UseBulkAssignmentSelectionValidationProps = {
  selectedCases: Record<string, BulkAssignmentCase>;
  setSelectedCases: (cases: Record<string, BulkAssignmentCase>) => void;
  filters: BulkAssignmentFiltersType;
};

const isUserOnCase = (caseItem: BulkAssignmentCase, employeeId: string, userGroup: CaseUserGroup) => {
  const employeesInGroup = caseItem.assignedEmployees[userGroup];
  return employeesInGroup?.some((u) => u.employeeId === employeeId) ?? false;
};

export const useBulkAssignmentSelectionValidation = ({
  selectedCases,
  setSelectedCases,
  filters,
}: UseBulkAssignmentSelectionValidationProps) => {
  const removeCases = (keysToRemove: string[], removedCaseNames: string[], reason: string) => {
    const newSelectedCases = { ...selectedCases };
    keysToRemove.forEach((key) => delete newSelectedCases[key]);
    setSelectedCases(newSelectedCases);

    const namesString = removedCaseNames.join(', ');
    const isPlural = removedCaseNames.length > 1;
    const caseLabel = isPlural ? 'Cases' : 'Case';

    toast.warning(`${caseLabel} ${namesString} deselected because ${reason}.`, { autoClose: 5000 });
  };

  const validateAndRemove = (predicate: (caseItem: BulkAssignmentCase) => boolean, reason: string) => {
    const keysToRemove: string[] = [];
    const removedCaseNames: string[] = [];

    Object.entries(selectedCases).forEach(([key, caseItem]) => {
      if (predicate(caseItem)) {
        keysToRemove.push(key);
        removedCaseNames.push(caseItem.caseName);
      }
    });

    if (keysToRemove.length > 0) {
      removeCases(keysToRemove, removedCaseNames, reason);
    }
  };

  const validateLastRequiredEmployee = (userToAssign: string | undefined, userToDetach: string | undefined) => {
    const tempFilters = {
      ...filters,
      employeeIdsToAssign: userToAssign ? [userToAssign] : [],
      employeeIdsToDetach: userToDetach ? [userToDetach] : [],
    };

    if (isDisabledDetachOnly(tempFilters)) {
      const hasOnlyOneRequiredEmployee = (caseItem: BulkAssignmentCase) => {
        const coordinators = caseItem.assignedEmployees?.[tempFilters.group[0]] || [];
        return coordinators.length === 1;
      };
      validateAndRemove(
        hasOnlyOneRequiredEmployee,
        `the case has only one ${GROUP_ROLE_NAME_MAP[tempFilters.group[0]]} assigned`,
      );
    }
  };

  const validateUserToAssign = (userToAssign: string | undefined) => {
    validateLastRequiredEmployee(userToAssign, filters.employeeIdsToDetach[0]);
    if (!userToAssign) return;
    const userGroup = filters.group[0];
    const isOnCase = (caseItem: BulkAssignmentCase) => isUserOnCase(caseItem, userToAssign, userGroup);
    validateAndRemove(isOnCase, 'the user is already on the case');
  };

  const validateUserToDetach = (userToDetach: string | undefined) => {
    if (!userToDetach) return;
    const userGroup = filters.group[0];

    const isNotOnCase = (caseItem: BulkAssignmentCase) => !isUserOnCase(caseItem, userToDetach, userGroup);
    validateAndRemove(isNotOnCase, 'the user is not on the case');

    validateLastRequiredEmployee(filters.employeeIdsToAssign[0], userToDetach);
  };

  return { validateAndRemove, validateUserToAssign, validateUserToDetach };
};
